import { gsap } from "@/lib/motion/gsap";

/** Original scroll-spine `qx` — clear transform on `from`, map center-to-center scale. */
export function mapRect(fromEl: Element, toEl: Element) {
  gsap.set(fromEl, { clearProps: "transform" });
  const from = fromEl.getBoundingClientRect();
  const to = toEl.getBoundingClientRect();
  return {
    scaleX: to.width / from.width,
    scaleY: to.height / from.height,
    x: to.left + to.width / 2 - (from.left + from.width / 2),
    y: to.top + to.height / 2 - (from.top + from.height / 2),
  };
}

export function coverScale(parent: HTMLElement, child: HTMLElement, extra: number) {
  const sx = parent.clientWidth / child.offsetWidth;
  const sy = parent.clientHeight / child.offsetHeight;
  return Math.max(sx, sy) + extra;
}

/** Original `Jx(hole, overlay)` — top-left that centers overlay on hole. */
export function centerDelta(hole: Element, overlay: Element) {
  const o = overlay.getBoundingClientRect();
  const h = hole.getBoundingClientRect();
  return {
    x: h.left + h.width / 2 - o.width / 2,
    y: h.top + h.height / 2 - o.height / 2,
  };
}

export function workBgClip() {
  const bgs = document.querySelectorAll<HTMLElement>(".work_bg");
  const w = window.innerWidth;
  const h = bgs[0]?.offsetHeight ?? 0;
  return `polygon(0px 0px, ${w}px 0px, ${w}px ${h}px, 0px ${h}px)`;
}

/**
 * Original `Qx(overlay, hole)`.
 * Must run *after* overlay x/y is applied — clip-path is local, getBoundingClientRect includes transform.
 */
export function clipTo(overlay: Element | Window | Document | string, hole: Element) {
  const target = hole;
  if (!overlay || overlay === window || overlay === document || overlay === "window") {
    const e = target.getBoundingClientRect();
    const left = e.left;
    const top = e.top;
    const right = window.innerWidth - e.right;
    const bottom = window.innerHeight - e.bottom;
    const x2 = window.innerWidth - right;
    const y2 = window.innerHeight - bottom;
    return `polygon(${left}px ${top}px, ${x2}px ${top}px, ${x2}px ${y2}px, ${left}px ${y2}px)`;
  }
  const parent =
    typeof overlay === "string" ? document.querySelector(overlay) : overlay;
  if (!parent || !(parent instanceof Element)) return "none";
  const r = parent.getBoundingClientRect();
  const a = target.getBoundingClientRect();
  const l = a.left - r.left;
  const top = a.top - r.top;
  const right = l + a.width;
  const b = top + a.height;
  return `polygon(${l}px ${top}px, ${right}px ${top}px, ${right}px ${b}px, ${l}px ${b}px)`;
}

/** Original F(): set x/y, then clipPath so Qx sees the transform. Use concrete values — function clipPath was collapsing to one polygon on every thumb. */
export function placeThumb(overlay: Element | null, hole: Element | null) {
  if (!overlay || !hole) return;
  const delta = centerDelta(hole, overlay);
  gsap.set(overlay, { x: delta.x, y: delta.y });
  gsap.set(overlay, { clipPath: clipTo(overlay, hole) });
}

export function canPlay(tl: gsap.core.Timeline) {
  return (!!tl.reversed() || !tl.isActive()) && tl.progress() !== 1;
}

export function canReverse(tl: gsap.core.Timeline) {
  return (!tl.reversed() || !tl.isActive()) && tl.progress() !== 0;
}
